import * as React from 'react';
import { cn } from '@/lib/utils';
import { Button } from './button';

const DEFAULT_MAX_SIZE = 2 * 1024 * 1024; // 2MB

interface FileUploadProps {
  accept?: string;
  maxSize?: number;
  value?: File | null;
  onChange: (file: File | null) => void;
  onError?: (error: string) => void;
  label?: string;
  description?: string;
  disabled?: boolean;
  className?: string;
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatMaxSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${Math.round(bytes / (1024 * 1024))} MB`;
}

function FileUpload({
  accept,
  maxSize = DEFAULT_MAX_SIZE,
  value,
  onChange,
  onError,
  label,
  description,
  disabled = false,
  className,
}: FileUploadProps) {
  const [isDragging, setIsDragging] = React.useState(false);
  const [previewUrl, setPreviewUrl] = React.useState<string | null>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const descriptionId = React.useId();

  React.useEffect(() => {
    if (value && value.type.startsWith('image/')) {
      const url = URL.createObjectURL(value);
      setPreviewUrl(url);
      return () => URL.revokeObjectURL(url);
    }
    setPreviewUrl(null);
    return undefined;
  }, [value]);

  const validateFile = (file: File): boolean => {
    if (accept) {
      const acceptedTypes = accept.split(',').map((t) => t.trim());
      if (!acceptedTypes.includes(file.type)) {
        onError?.(`Invalid file type. Accepted types: ${acceptedTypes.join(', ')}`);
        return false;
      }
    }

    if (file.size > maxSize) {
      onError?.(`File size exceeds ${formatMaxSize(maxSize)} limit`);
      return false;
    }

    return true;
  };

  const handleFile = (file: File) => {
    if (validateFile(file)) {
      onChange(file);
    }
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      handleFile(file);
    }
  };

  const handleDragEnter = (event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    if (!disabled) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);

    if (disabled) return;

    const file = event.dataTransfer.files[0];
    if (file) {
      handleFile(file);
    }
  };

  const handleRemove = () => {
    onChange(null);
    if (inputRef.current) {
      inputRef.current.value = '';
    }
  };

  const handleZoneClick = () => {
    if (!disabled && inputRef.current) {
      inputRef.current.click();
    }
  };

  return (
    <div data-testid="file-upload-container" className={cn('w-full', className)}>
      {label && (
        <label className="mb-2 block text-sm font-medium">{label}</label>
      )}

      {value ? (
        <div className="rounded-lg border bg-muted/50 p-4">
          <div className="flex items-start gap-4">
            {previewUrl && (
              <img
                src={previewUrl}
                alt="File preview"
                className="h-16 w-16 rounded-md object-cover"
              />
            )}
            <div className="flex-1 min-w-0">
              <p className="truncate text-sm font-medium">{value.name}</p>
              <p className="text-sm text-muted-foreground">
                {formatFileSize(value.size)}
              </p>
            </div>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={handleRemove}
              aria-label="Remove file"
            >
              Remove
            </Button>
          </div>
        </div>
      ) : (
        <div
          data-testid="drop-zone"
          role="button"
          tabIndex={disabled ? -1 : 0}
          onClick={handleZoneClick}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              handleZoneClick();
            }
          }}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          className={cn(
            'flex cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed p-8 transition-colors',
            'hover:border-primary/50 hover:bg-muted/50',
            isDragging && 'border-primary bg-primary/5',
            disabled && 'cursor-not-allowed opacity-50'
          )}
        >
          <UploadIcon className="mb-4 h-10 w-10 text-muted-foreground" />
          <p className="text-sm text-muted-foreground">
            Click to upload or drag and drop
          </p>
          {description && (
            <p
              id={descriptionId}
              className="mt-1 text-xs text-muted-foreground"
            >
              {description}
            </p>
          )}
        </div>
      )}

      <input
        ref={inputRef}
        type="file"
        data-testid="file-input"
        accept={accept}
        disabled={disabled}
        onChange={handleInputChange}
        className="sr-only"
        aria-describedby={description ? descriptionId : undefined}
      />
    </div>
  );
}

function UploadIcon({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
      className={className}
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5"
      />
    </svg>
  );
}

export { FileUpload };
export type { FileUploadProps };
